import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const fixTextContrastSkill: Skill = {
    name: SKILL_NAMES.fixTextContrast,
    description:
        'Fixing text that is hard to read against what sits behind it. Load when a tool finding reports low text contrast.',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Fixing Text Contrast

A contrast finding says the text and what is behind it are too close in brightness. It does
**not** say the media is too bright — that is one cause among several, and reaching for it
first is how a scene ends up dimmed into mud.

## The finding already tells you what the text sits on
Read it before you touch anything. It names the controlling backdrop and which dial moves it:
- **Media** — the photo or video sits directly behind the text, with nothing drawn in between.
- **A design-drawn backdrop** — a rectangle, circle, or graphic the design draws.
- **A background the text asset draws for itself** — a box or bar that is part of the text.
- **A background graphic an enclosing group draws** — sits behind a whole text group, often translucent.

Only the first of those can be fixed with brightness. **Trust what the finding says** and go
straight to the fix — reach for \`${TOOL_NAMES.imageAsk}\` only if it leaves you genuinely
unsure what you are looking at, or if a first attempt did not move the ratio.

## Text on a design-drawn panel: brightness is the wrong dial
The panel is the backdrop. The media underneath it is not what the text sits on, so dimming
the media changes nothing the reader sees and the finding will come back unchanged. Recolour the
**text** with \`${TOOL_NAMES.setTextColor}\` (handle \`text\`) so it opposes the panel, or recolour
the panel itself with \`${TOOL_NAMES.setAssetColor}\` (the video state lists each colour it carries
under the property name that tool takes). Do not touch media brightness.

## Text on its own or its group's background box: the box is your first lever
The box behind the glyphs belongs to the text asset or to the group wrapping it, not to the
scene, so the media beneath it is largely invisible to the reader. Dimming media here is the
classic way to loop: the ratio barely budges, the finding returns, and nothing the reader can
see has changed.

The box is addressable. A text asset's video-state entry carries a \`colors\` map — every colour it
draws, each with a \`handle\` (\`box_1\`, …), a \`role\`, its \`value\`, \`alpha\`, and \`fillType\`. A
backdrop an enclosing GROUP draws instead lives on that GROUP's own \`colors\` map, addressed by
naming the group (not the text inside it). The box entries are the ones whose role is \`backdrop\`,
\`underline\`, or \`accent_bar\`. Everything is set with \`${TOOL_NAMES.setTextColor}\`, addressed by
\`handle\` on whichever asset lists it. Fix the contrast at the box:
- **Recolour the backdrop box** — \`${TOOL_NAMES.setTextColor}\` with the \`handle\` of the entry whose
  role is \`backdrop\` (only a backdrop sits behind the glyphs; recolouring an \`underline\` or
  \`accent_bar\` won't move the ratio) and a \`color\` that opposes the text. **Prefer this over dimming
  media toward black** — it changes only what sits behind the glyphs and leaves the rest of the image
  intact.
- **A \`fillType: gradient\` backdrop can't be recoloured here** — its colour is ignored at render
  time, so \`${TOOL_NAMES.setTextColor}\` rejects it. Go straight to the text colour.

If the box can't be made to work, recolour the **text** with \`${TOOL_NAMES.setTextColor}\`
(handle \`text\`) instead. Do not dim media for a box backdrop.

## Bare text on media: dim the media OR darken the glyph — the media's tone decides
When the text sits directly on media with nothing drawn behind it you have two clean levers, and the
media's overall brightness tells you which one wins:
- **Media reads dark** → keep the light text and dim any bright patch with \`${TOOL_NAMES.editMedia}\`
  (\`brightness\`), one step at a time: 1.0 → 0.75 → 0.5.
- **Media reads bright or mid-tone** (a light photo, a pale or mixed gradient) → do not chase the
  brightness down into mud. Flip the **glyph** to near-black with \`${TOOL_NAMES.setTextColor}\`
  (handle \`text\`) and leave the media bright. Dark text on a bright background clears the ratio with
  room to spare and keeps the image's punch — it is usually the stronger fix for a light title over a
  mid-tone gradient, where dimming only crawls toward the floor.

**0.5 is the floor, and never dim the same scene twice.** If you have already lowered brightness on a
scene and the finding is still there, brightness is not the answer. Do **not** take another step, and
do **not** settle for light text on the dimmed result (a dimmed mid-tone is still not dark enough for
white text): **restore the brightness to 1.0 and flip the glyph to near-black instead**, then
escalate.

## Escalation, in order
1. **Flip the text to its opposite pole** — \`${TOOL_NAMES.setTextColor}\` (handle \`text\`): near-black
   over a light or mid-tone background, white over a genuinely dark one. Judge the pole from the
   media's own tone, not from a brightness you just lowered — if you dimmed toward white text and it
   still falls short, put the brightness back to 1.0 and go near-black instead. Half-measures (grey on
   grey) buy nothing.
2. **Move the text somewhere calmer** — \`${TOOL_NAMES.setTextPositionPreset}\`. A photo usually
   has a plain region; put the text there rather than fighting the busy one.
3. **Swap the media** — \`${TOOL_NAMES.searchMedia}\` for an image with an uncluttered area, then
   \`${TOOL_NAMES.setMedia}\`.

## Stop after two attempts per finding
The reported ratio is a mean over the text's whole box, so a small residual shortfall on a
busy photo is not worth a third round. After two attempts, stop and tell the user which
scene you left short and why.

## Do not
- **Don't reach for \`${TOOL_NAMES.setGlobalColorGrading}\`** to fix one scene. It redraws the
  whole video to solve a local problem — see the ${SKILL_NAMES.applyBrandKit} skill.
- **Don't change two dials for one finding.** If you dimmed and then recoloured the text, put
  the brightness back before you finish.`;
    },
};
